import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Briefcase, GraduationCap, MapPin, Clock3, ArrowDown, FileUp, CheckCircle, Loader2 } from "lucide-react";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import api, { formatApiError } from "@/lib/api";

const ROLES = [
  {
    id: "fundraiser",
    title: "Fundraiser",
    type: "Full-time",
    location: "Hyderabad · Hybrid",
    commitment: "40 hrs / week",
    blurb: "Own donor relationships and campaigns that keep classrooms funded — from first hello to renewal.",
    points: ["Build and manage a donor pipeline", "Run giving campaigns end-to-end", "Report impact back to donors"],
  },
  {
    id: "social-media",
    title: "Social Media Associate",
    type: "Internship",
    location: "Hybrid · 3 months",
    commitment: "15 hrs / week",
    blurb: "Turn paint days and classroom wins into stories the internet can't scroll past.",
    points: ["Shoot and edit field content", "Run our Instagram, LinkedIn & YouTube", "Grow a community of supporters"],
  },
  {
    id: "project-manager",
    title: "Project Manager",
    type: "Full-time",
    location: "Hyderabad",
    commitment: "40 hrs / week",
    blurb: "Run school programs end-to-end — schedules, volunteer teachers, outcomes, and the paperwork that proves them.",
    points: ["Coordinate 3+ partner schools", "Track learning outcomes", "Lead volunteers and fellows"],
  },
  {
    id: "coordinator",
    title: "Program Coordinator",
    type: "Full-time or Internship",
    location: "Field · Hyderabad schools",
    commitment: "Flexible",
    blurb: "Be the bridge between LLI and our partner schools — the person headmasters call first.",
    points: ["Weekly school visits", "Logistics for drives and events", "Support teachers on the ground"],
  },
];

const inputCls =
  "w-full bg-bone border border-forest/25 px-4 py-3 text-sm text-ink placeholder:text-moss/50 focus:outline-none focus:border-terra focus:ring-1 focus:ring-terra transition";

const Careers = () => {
  const formRef = useRef(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", role: ROLES[0].title, message: "" });
  const [resume, setResume] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const applyFor = (role) => {
    setForm((f) => ({ ...f, role }));
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let resume_path = "";
      if (resume) {
        const data = new FormData();
        data.append("file", resume);
        const { data: up } = await api.post("/careers/resume", data);
        resume_path = up.path;
      }
      const role = ROLES.find((r) => r.title === form.role);
      await api.post("/applications", {
        ...form,
        role_type: role?.type || "Full-time",
        resume_path,
      });
      setDone(true);
      toast.success("Application received! We'll be in touch soon.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-bone min-h-screen" data-testid="careers-page">
      <div className="noise-overlay" />
      <Navbar />
      <main className="pt-[72px]">
        <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-12">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <span className="text-xs font-bold tracking-[0.3em] uppercase text-terra">Careers at LLI</span>
            <h1 className="font-serif font-bold tracking-tighter text-forest text-4xl sm:text-5xl lg:text-6xl mt-4 max-w-3xl" data-testid="careers-heading">
              Work where the <span className="italic text-terra">impact</span> is visible every Monday.
            </h1>
            <p className="text-base lg:text-lg text-moss leading-relaxed mt-6 max-w-2xl">
              We're a small team with a big classroom count. Whether you're starting out
              or bringing years of experience, if government schools in Hyderabad are
              where you want to make a difference — we should talk.
            </p>
          </motion.div>
        </section>

        <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-20">
          <div className="grid md:grid-cols-2 gap-6" data-testid="roles-grid">
            {ROLES.map((role, i) => (
              <motion.article
                key={role.id}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="border border-forest/20 bg-bone p-8 flex flex-col hover:-translate-y-1.5 hover:shadow-xl transition-all duration-300"
                data-testid={`role-card-${role.id}`}
              >
                <div className="flex items-center justify-between gap-4 mb-5">
                  <span className={`flex items-center gap-2 text-xs font-bold px-3 py-1.5 border ${role.type === "Internship" ? "border-terra text-terra" : "border-forest text-forest"}`} data-testid={`role-type-${role.id}`}>
                    {role.type === "Internship" ? <GraduationCap size={14} /> : <Briefcase size={14} />}
                    {role.type}
                  </span>
                  <span className="text-xs text-moss flex items-center gap-1.5"><MapPin size={13} /> {role.location}</span>
                </div>
                <h2 className="font-serif font-bold text-forest text-3xl tracking-tight">{role.title}</h2>
                <p className="text-sm text-moss leading-relaxed mt-3 flex-1">{role.blurb}</p>
                <ul className="mt-5 space-y-2">
                  {role.points.map((p) => (
                    <li key={p} className="text-sm text-ink flex items-start gap-2.5">
                      <span className="w-1.5 h-1.5 bg-terra mt-1.5 shrink-0" /> {p}
                    </li>
                  ))}
                </ul>
                <div className="mt-6 pt-5 border-t border-forest/15 flex items-center justify-between gap-4">
                  <span className="text-xs text-moss flex items-center gap-1.5"><Clock3 size={13} /> {role.commitment}</span>
                  <button onClick={() => applyFor(role.title)} data-testid={`role-apply-${role.id}`}
                    className="flex items-center gap-2 bg-forest text-bone text-sm font-bold px-5 py-2.5 hover:bg-terra transition-colors duration-200">
                    Apply <ArrowDown size={14} />
                  </button>
                </div>
              </motion.article>
            ))}
          </div>
        </section>

        <section ref={formRef} className="bg-bonealt/60 border-t border-forest/15 py-20 scroll-mt-20" data-testid="application-section">
          <div className="max-w-2xl mx-auto px-6 lg:px-10">
            <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7 }} className="mb-10 text-center">
              <h2 className="font-serif font-bold tracking-tighter text-forest text-4xl lg:text-5xl" data-testid="application-heading">
                Send your application
              </h2>
              <p className="text-base text-moss mt-4">Two minutes now, a different Monday soon.</p>
            </motion.div>

            {done ? (
              <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} className="border border-forest/20 bg-bone p-10 text-center" data-testid="application-success">
                <CheckCircle size={52} weight="fill" className="text-terra mx-auto mb-5" />
                <h3 className="font-serif font-bold text-forest text-3xl tracking-tight">Application received.</h3>
                <p className="text-base text-moss mt-3 leading-relaxed">
                  Thank you for wanting to light lamps with us. Our team reviews every
                  application personally and will write back within a week.
                </p>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="border border-forest/20 bg-bone p-8 space-y-4" data-testid="application-form">
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls} data-testid="application-role-select">
                  {ROLES.map((r) => <option key={r.id} value={r.title}>{r.title} — {r.type}</option>)}
                </select>
                <div className="grid sm:grid-cols-2 gap-4">
                  <input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="application-name-input" />
                  <input required type="email" placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="application-email-input" />
                </div>
                <input required type="tel" placeholder="Phone number" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} data-testid="application-phone-input" />
                <textarea rows={4} placeholder="Why this role? Anything we should know (optional)" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={inputCls} data-testid="application-message-input" />
                <label className="flex items-center justify-center gap-2 border border-dashed border-forest/30 px-4 py-4 text-sm font-bold text-forest cursor-pointer hover:border-terra hover:text-terra transition-colors" data-testid="application-resume-label">
                  <FileUp size={16} />
                  {resume ? resume.name : "Attach resume (PDF, optional)"}
                  <input type="file" accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" className="hidden"
                    onChange={(e) => setResume(e.target.files?.[0] || null)} data-testid="application-resume-input" />
                </label>
                <button type="submit" disabled={busy} data-testid="application-submit-btn"
                  className="w-full bg-forest text-bone font-bold py-4 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200 disabled:opacity-60 disabled:translate-y-0 flex items-center justify-center gap-2">
                  {busy && <Loader2 size={16} className="animate-spin" />}
                  {busy ? "Sending…" : "Submit Application"}
                </button>
              </form>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Careers;
